import React, { useState, useEffect } from "react";
import {
  Table,
  TextInput,
  Button,
  ActionIcon,
  Group,
  Title,
  Text,
  Stack,
  Loader,
  UnstyledButton,
} from "@mantine/core";
import {
  IconPlus,
  IconTrash,
  IconDeviceFloppy,
  IconSelector,
  IconChevronUp,
  IconChevronDown,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { settingsServices } from "../../services/settingsServices";

const Notebook = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Sorting state
  const [sortBy, setSortBy] = useState(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);

  useEffect(() => {
    fetchNotebook();
  }, []);

  const fetchNotebook = async () => {
    setLoading(true);
    try {
      const response = await settingsServices.getNotebook();
      let data = response.notebook;

      if (typeof data === "string" && data.trim()) {
        try {
          const parsed = JSON.parse(data);
          if (parsed !== null) data = parsed;
        } catch (e) {
          data = data
            .split("\n")
            .filter((l) => l.trim())
            .map((line) => ({
              geo: "",
              partner: "",
              offer: "",
              garant: "",
              comment: line,
            }));
        }
      }

      if (Array.isArray(data)) {
        const normalized = data.map((item) => {
          if (typeof item === "string") {
            return {
              geo: "",
              partner: "",
              offer: "",
              garant: "",
              comment: item,
            };
          }
          if (typeof item === "object" && item !== null) {
            return {
              geo: item.geo || "",
              partner: item.partner || "",
              offer: item.offer || "",
              garant: item.garant || "",
              comment: item.comment || item.value || "",
            };
          }
          return { geo: "", partner: "", offer: "", garant: "", comment: "" };
        });
        setRows(normalized);
      } else {
        setRows([]);
      }
    } catch (error) {
      notifications.show({
        title: "Помилка",
        message: "Не вдалося завантажити записник",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  const addRow = () => {
    setRows([
      ...rows,
      { geo: "", partner: "", offer: "", garant: "", comment: "" },
    ]);
  };

  const removeRow = (index) => {
    const newRows = [...rows];
    newRows.splice(index, 1);
    setRows(newRows);
  };

  const updateRow = (index, field, newValue) => {
    const newRows = [...rows];
    newRows[index] = { ...newRows[index], [field]: newValue };
    setRows(newRows);
  };

  const handleSort = (field) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);

    const sorted = [...rows].sort((a, b) => {
      const valA = (a[field] || "").toString().toLowerCase();
      const valB = (b[field] || "").toString().toLowerCase();

      if (!valA && valB) return 1;
      if (valA && !valB) return -1;

      return reversed ? valB.localeCompare(valA) : valA.localeCompare(valB);
    });
    setRows(sorted);
  };

  const saveNotebook = async () => {
    setSaving(true);
    try {
      await settingsServices.saveNotebook(rows);
      notifications.show({
        title: "Успішно",
        message: "Записник збережено",
        color: "green",
      });
    } catch (error) {
      notifications.show({
        title: "Помилка",
        message: "Не вдалося зберегти записник",
        color: "red",
      });
    } finally {
      setSaving(false);
    }
  };

  const SortHeader = ({ field, label, width }) => {
    const isSorted = sortBy === field;
    const Icon = isSorted
      ? reverseSortDirection
        ? IconChevronDown
        : IconChevronUp
      : IconSelector;

    return (
      <Table.Th style={{ width: width || "auto" }}>
        <UnstyledButton
          onClick={() => handleSort(field)}
          style={{ width: "100%" }}
        >
          <Group justify="space-between" wrap="nowrap">
            <Text fw={700} size="sm">
              {label}
            </Text>
            <Icon
              size={14}
              stroke={1.5}
              style={{ opacity: isSorted ? 1 : 0.3 }}
            />
          </Group>
        </UnstyledButton>
      </Table.Th>
    );
  };

  return (
    <Stack gap="md" mt="xl">
      <Group justify="space-between">
        <Title order={3}>Мій записник</Title>
        <Group>
          <Button
            leftSection={<IconPlus size={16} />}
            variant="light"
            onClick={addRow}
          >
            Додати рядок
          </Button>
          <Button
            leftSection={<IconDeviceFloppy size={16} />}
            onClick={saveNotebook}
            loading={saving}
            color="green"
          >
            Зберегти
          </Button>
        </Group>
      </Group>

      <Table.ScrollContainer minWidth={800}>
        <Table striped highlightOnHover withTableBorder verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <SortHeader field="geo" label="ГЕО" width={120} />
              <SortHeader field="partner" label="Партнерка" />
              <SortHeader field="offer" label="Оффер" />
              <Table.Th style={{ width: 120 }}>Гарант (%)</Table.Th>
              <Table.Th>Коментар</Table.Th>
              <Table.Th style={{ width: 50 }}></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={6} py="xl" ta="center">
                  <Loader size="sm" />
                </Table.Td>
              </Table.Tr>
            ) : rows.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={6} py="xl" ta="center">
                  <Text c="dimmed" size="sm">
                    У вас ще немає записів. Натисніть "Додати рядок", щоб
                    почати.
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              rows.map((row, index) => (
                <Table.Tr key={index}>
                  <Table.Td>
                    <TextInput
                      size="xs"
                      placeholder="ГЕО"
                      value={row.geo}
                      onChange={(e) =>
                        updateRow(index, "geo", e.currentTarget.value)
                      }
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      size="xs"
                      placeholder="Партнерка"
                      value={row.partner}
                      onChange={(e) =>
                        updateRow(index, "partner", e.currentTarget.value)
                      }
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      size="xs"
                      placeholder="Оффер"
                      value={row.offer}
                      onChange={(e) =>
                        updateRow(index, "offer", e.currentTarget.value)
                      }
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      size="xs"
                      placeholder="%"
                      value={row.garant}
                      onChange={(e) =>
                        updateRow(index, "garant", e.currentTarget.value)
                      }
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      size="xs"
                      placeholder="Коментар..."
                      value={row.comment}
                      onChange={(e) =>
                        updateRow(index, "comment", e.currentTarget.value)
                      }
                    />
                  </Table.Td>
                  <Table.Td>
                    <ActionIcon
                      color="red"
                      variant="subtle"
                      onClick={() => removeRow(index)}
                      title="Видалити"
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Stack>
  );
};

export default Notebook;
